// Helpers for shaping a raw Transaction into the row form the table consumes.

import type { Transaction } from '@/modules/investing/types'

// Investor-view rule: rows of these types render nothing in the actions cell,
// because the underlying edit page does not handle them and clicking "View"
// navigates to a route that never loads. See ticket #2134.
export const INVESTOR_HIDDEN_TRANSACTION_TYPES = new Set([
  'management_fee',
  'other_fee',
  'syndication_fee',
  'recycled_funds',
  'operating_income',
  'operating_expense',
])

export function hideActionsForInvestor(
  item: { display_type?: string | null; type?: string | null } | null | undefined,
): boolean {
  if (!item) return false
  return INVESTOR_HIDDEN_TRANSACTION_TYPES.has(item.display_type ?? item.type ?? '')
}

/**
 * Derive the type used for visual display. Distinguishes scheduled vs sent
 * capital calls and cash vs asset distributions; everything else is its raw
 * type.
 */
export function getDisplayType(t: Transaction): string {
  if (t.type === 'capital_call') {
    return !t.sent_at ? 'scheduled_call' : 'capital_call'
  }
  if (t.type === 'distribution') {
    if (t.batch_type === 'asset') return 'asset_funds'
    return 'cash_distribution'
  }
  return t.type
}

/**
 * Compute the group key used by the table. Rows with the same key are grouped
 * together. Three group identities are supported:
 *   - batch:<id>         transfers sharing an investor_set_transaction batch
 *   - loss:<co>:<date>   loss investments on the same company / date
 *   - <type>:<id>        a row that's its own group of one
 */
export function groupKeyFor(t: Transaction, company: { id: string | number } | null): string {
  if (t.transaction_batch_id) return `batch:${t.transaction_batch_id}`
  if (t.type === 'loss_investment' && company) return `loss:${company.id}:${t.date}`
  return `${t.type}:${t.id}`
}
